import Alpine from "alpinejs";
import { pauseAllMedia } from "./../utils/media";

export default ({
	productID,
	shouldUpdateURL = false,
	shouldUpdateMedia = true,
	selectedMediaID,
	selectedVariantID,
}) => ({
	// STATE
	// Cache product info fetch requests for quick re-rendering.
	cache: new Map(),
	// Track if the variant is being updated.
	isUpdatingVariant: false,
	variants: [],
	selectedVariant: false,
	selectedMediaID,
	option1: null,
	option2: null,
	option3: null,

	// GETTERS
	get sectionIDs() {
		return [
			...document.querySelectorAll(`[data-variant-section="${productID}"]`),
		].map((el) => el.dataset.id);
	},

	// LIFECYCLE
	init() {
		const params = new URLSearchParams(window.location.search);
		// Parse the variant JSON and initialize the selectedVariant.
		this.variants = JSON.parse(this.$refs.productJSON.textContent);
		this.selectedVariant = this.variants.find((variant) => {
			return variant.id == selectedVariantID;
		});
		this.option1 = this.selectedVariant?.option1 || params.get("option1");
		this.option2 = this.selectedVariant?.option2 || params.get("option2");
		this.option3 = this.selectedVariant?.option3 || params.get("option3");

		if (this.option1 || this.option2 || this.option3) {
			this.updateSelectedVariant();
		}

		// Pause videos when the selectedMediaID changes.
		this.$watch("selectedMediaID", (newMediaID, oldMediaID) => {
			if (newMediaID != oldMediaID) pauseAllMedia();
		});
	},

	// METHODS
	render(sections) {
		const patches = [];

		Object.entries(sections).forEach(([id, markup]) => {
			const selector = `[data-variant-section="${productID}"][data-id="${id}"]`;

			const toReplace = document.querySelectorAll(selector);
			const toHTML = new DOMParser()
				.parseFromString(markup, "text/html")
				.querySelector(selector).outerHTML;

			toReplace.forEach((el) => {
				const patch = new Promise((resolve, reject) => {
					Alpine.morph(el, toHTML, {
						updated() {
							resolve(true);
						},
					});
				});

				patches.push(patch);
			});
		});

		return Promise.all(patches);
	},
	updateSelectedVariant() {
		// Set selectedVariant to the variant that matches all selected options.
		this.selectedVariant = this.variants.find((variant) => {
			return (
				this.option1 == variant.option1 &&
				this.option2 == variant.option2 &&
				this.option3 == variant.option3
			);
		});

		// Update the hidden input with the selectedVariant ID.
		if (this.selectedVariant) {
			this.$refs.productID.value = this.selectedVariant.id;
		}
	},
	updateMedia() {
		// Cancel if it shouldn't update, there is no selected variant, or there is no featured image attached to the selected variant.
		if (
			!shouldUpdateMedia ||
			!this.selectedVariant ||
			!this.selectedVariant.featured_media
		)
			return;

		// Set selectedMediaID to the selectedVariant's featured image id.
		this.selectedMediaID = this.selectedVariant.featured_media.id;
	},
	async updatePickupAvailability() {
		if (!this.selectedVariant) return;

		// If cache has variant markup, render it.
		const key = this.selectedVariant.id;
		if (this.cache.has(key)) {
			this.render(this.cache.get(key));
			return;
		}

		// Indicate we're loading data.
		this.isUpdatingVariant = true;

		// Fetch availability.
		const response = await fetch(
			`${window.Shopify.routes.root}variants/${
				this.selectedVariant.id
			}/?sections=${this.sectionIDs.join(",")}`
		);

		// If response error, stop and hide suggestions.
		if (!response.ok) {
			console.error(response.status);
			this.isUpdatingVariant = false;
			return;
		}

		const json = await response.json();

		// Render results.
		this.render(json);

		// Cache results and remove loader.
		this.cache.set(key, json);
		this.isUpdatingVariant = false;
	},
	updateURL() {
		// Cancel if it shouldn't update.
		if (!shouldUpdateURL) return;

		const params = new URLSearchParams(window.location.search);

		if (params.has("view", "multistep")) {
			["option1", "option2", "option3"].forEach((opt) => {
				params.delete(opt);
				this[opt] && params.set(opt, this[opt]);
			});
		} else {
			params.delete("variant");
			this.selectedVariant && params.set("variant", this.selectedVariant.id);
		}

		// Update the url with the selected variant's ID.
		window.history.replaceState(
			{},
			"",
			`${document.location.origin}${
				document.location.pathname
			}?${params.toString()}`
		);
	},

	async onSubmit(e) {
		// Grab the form's data.
		const data = new FormData(e.target);

		// Add to cart.
		await Alpine.store("cart").add(data);
	},

	// ELEMENTS
	productRoot: {
		"@change"() {
			this.updateSelectedVariant();
			this.updateMedia();
			this.updateURL();
			this.updatePickupAvailability();
		},
	},

	productButton: {
		[":disabled"]() {
			// Disable button if no selectedVariant or not available.
			return !this.selectedVariant || !this.selectedVariant.available;
		},
		["x-text"]() {
			// If no selectedVariant, unavailable.
			if (!this.selectedVariant) {
				return window.variantStrings.unavailable;
			}

			// If selectedVariant is available, add to cart.
			if (this.selectedVariant.available) {
				return window.variantStrings.addToCart;
			}

			// If selectedVariant is unavailable, sold out.
			return window.variantStrings.soldOut;
		},
	},
});
